// compareTable.ts — pure row/best-value logic for Vergleich's Kennzahlen comparison
// TABLE (VergleichView.tsx): metrics as ROWS, scenarios as COLUMNS, replacing the earlier
// column-of-cards layout (which the user found broken — gaps/overflow at various widths).
// Given each scenario's current RunResult (+ mode-specific extras a plain RunResult
// doesn't carry: the backtest fund's own observed price CAGR, fetched separately by
// VergleichView), this module decides what every cell SHOWS and, where a row has a
// meaningful "better" direction, which scenario(s) currently hold the best value — so the
// component only renders what this module already worked out, instead of re-deriving
// comparison logic inline in JSX.
//
// A row's meaning is kept consistent across forecast/backtest columns (same task
// requirement the old card layout satisfied too): "Rendite p.a." shows the forecast's
// return ASSUMPTION for a forecast column vs. the backtest's OBSERVED period return for a
// backtest column — two different kinds of number (assumption vs. measured outcome), so
// that row is never best-highlighted (direction 'none'); same for TER, which for backtest
// is only an ESTIMATE already baked into the price (D-007), not a directly comparable
// deducted cost the way forecast's TER is. Every other row is a plain, honestly
// comparable "more/less is better" figure.
import type { SimulationResult } from '@etf-cockpit/engine';
import { formatBp, formatCents, formatDateDe, formatPercent } from './format.js';
import { deflateResult } from './realTerms.js';
import type { RunResult } from './scenario.js';
import type { WaterfallMode } from './waterfall-geometry.js';

export interface CompareColumnInput {
  id: string;
  run: RunResult;
  /** Backtest-only: the fund's own time-weighted price CAGR over the used range (see
   * lib/backtestReturns.ts's backtestPriceCagr) — fetched separately by VergleichView
   * (read-only history fetch), not part of RunResult itself. null while not (yet)
   * available or not applicable (forecast mode, or history still loading). */
  priceCagr: number | null;
}

export interface CompareCell {
  id: string;
  text: string;
  /** True when this cell holds the (possibly tied-)best value for its row. Always false
   * for a direction:'none' row, and always false when fewer than 2 scenarios have a
   * comparable (non-null) value for this row — "best" is only meaningful once there's
   * something to compare against. */
  isBest: boolean;
}

export interface CompareRow {
  key: string;
  label: string;
  cells: CompareCell[];
}

type Direction = 'high' | 'low' | 'none';

interface RowContext {
  display: SimulationResult | null;
  mode: WaterfallMode | undefined;
  run: RunResult;
  priceCagr: number | null;
}

interface RowDef {
  key: string;
  label: string;
  direction: Direction;
  value: (ctx: RowContext) => { raw: number | null; text: string };
}

function displayTerCents(result: SimulationResult, mode: WaterfallMode | undefined): number | null {
  if (mode === 'forecast') return result.waterfall.terCents;
  return result.waterfall.terInformationalCents ?? null;
}

const ROW_DEFS: RowDef[] = [
  {
    key: 'wealth',
    label: 'Endvermögen',
    direction: 'high',
    value: ({ display }) =>
      display
        ? { raw: display.waterfall.netWealthCents, text: formatCents(display.waterfall.netWealthCents) }
        : { raw: null, text: '—' },
  },
  {
    key: 'contributed',
    label: 'Eingezahlt',
    direction: 'none',
    value: ({ display }) =>
      display
        ? { raw: display.waterfall.contributedCents, text: formatCents(display.waterfall.contributedCents) }
        : { raw: null, text: '—' },
  },
  {
    key: 'gain',
    label: 'Kursgewinn',
    direction: 'high',
    value: ({ display }) =>
      display
        ? { raw: display.waterfall.grossGainCents, text: formatCents(display.waterfall.grossGainCents) }
        : { raw: null, text: '—' },
  },
  {
    key: 'tax',
    label: 'Steuern gesamt',
    direction: 'low',
    value: ({ display }) =>
      display
        ? { raw: display.summary.totalKestCents, text: formatCents(display.summary.totalKestCents) }
        : { raw: null, text: '—' },
  },
  {
    key: 'fees',
    label: 'Gebühren',
    direction: 'low',
    value: ({ display }) =>
      display ? { raw: display.waterfall.feesCents, text: formatCents(display.waterfall.feesCents) } : { raw: null, text: '—' },
  },
  {
    key: 'ter',
    label: 'TER',
    direction: 'none',
    value: ({ display, mode }) => {
      if (!display) return { raw: null, text: '—' };
      const cents = displayTerCents(display, mode);
      return { raw: null, text: cents === null ? '—' : formatCents(cents) };
    },
  },
  {
    key: 'returnPa',
    label: 'Rendite p.a.',
    direction: 'none',
    value: ({ mode, run, priceCagr }) => {
      if (mode === 'forecast' && run.config) return { raw: null, text: formatBp(run.config.annualReturnBp) };
      if (mode === 'backtest') return { raw: null, text: priceCagr !== null ? formatPercent(priceCagr) : '—' };
      return { raw: null, text: '—' };
    },
  },
  {
    key: 'breakeven',
    label: 'Break-even',
    // Earliest date = best (reaches the fees/TER break-even soonest); ISO dates compare
    // correctly as epoch millis via Date.parse.
    direction: 'low',
    value: ({ display }) => {
      const date = display?.crossover.cumulativeDate ?? null;
      return date ? { raw: Date.parse(date), text: formatDateDe(date) } : { raw: null, text: '—' };
    },
  },
];

/** Builds one row per ROW_DEFS entry, one cell per input column, in column order.
 * `inflationBp` deflates every column's result identically before reading values (same
 * "in heutiger Kaufkraft" toggle VergleichView already surfaces) — 0 when the
 * Inflationsbereinigung toggle is off, in which case deflateResult is a cheap no-op. */
export function buildCompareRows(columns: CompareColumnInput[], inflationBp: number): CompareRow[] {
  return ROW_DEFS.map((def) => {
    const raws: { id: string; raw: number }[] = [];
    const cells: CompareCell[] = columns.map((col) => {
      const display = col.run.status === 'success' && col.run.result ? deflateResult(col.run.result, inflationBp) : null;
      const { raw, text } = def.value({ display, mode: col.run.mode, run: col.run, priceCagr: col.priceCagr });
      if (raw !== null) raws.push({ id: col.id, raw });
      return { id: col.id, text, isBest: false };
    });
    if (def.direction !== 'none' && raws.length >= 2) {
      const best = def.direction === 'high' ? Math.max(...raws.map((r) => r.raw)) : Math.min(...raws.map((r) => r.raw));
      const bestIds = new Set(raws.filter((r) => r.raw === best).map((r) => r.id));
      for (const cell of cells) {
        if (bestIds.has(cell.id)) cell.isBest = true;
      }
    }
    return { key: def.key, label: def.label, cells };
  });
}

/** Whether any column is a backtest result — controls the "* Backtest-TER ist geschätzt…"
 * (D-007) footnote under the table, same condition the old column-card layout used. */
export function anyBacktestColumn(columns: CompareColumnInput[]): boolean {
  return columns.some((c) => c.run.status === 'success' && c.run.mode === 'backtest');
}
